"use client";

import React, { useState, useCallback } from "react";
import Link from "next/link";
import { ArrowRight, Calculator, CheckCircle, ChevronRight, Link2, RotateCcw, Zap } from "lucide-react";
import SeoChip from "@components/ui/SeoChip";
import type {
  PricingCalculatorData,
  CalculatorInput,
  RangeInput,
} from "./types";

interface InputState {
  [id: string]: number | boolean;
}

function buildInitialState(inputs: CalculatorInput[]): InputState {
  const state: InputState = {};
  for (const input of inputs) {
    if (input.type === "range") {
      state[input.id] = input.default;
    } else {
      state[input.id] = false;
    }
  }
  return state;
}

function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

interface PricingCalculatorProps {
  data: PricingCalculatorData;
  /* When set, the price CTA opens the lead form with `summary` — the
     configuration the visitor just assembled — instead of linking to
     `data.ctaHref`. Optional so the anchor behaviour stays the default. */
  onRequestQuote?: (summary: string) => void;
}

export default function PricingCalculator({
  data,
  onRequestQuote,
}: PricingCalculatorProps) {
  const [inputState, setInputState] = useState<InputState>(() =>
    buildInitialState(data.inputs),
  );
  const [copied, setCopied] = useState(false);

  const sheets = inputState.sheets as number;
  const columns = inputState.columns as number;
  const extraSource = inputState.extraSource as boolean;

  const handleRangeChange = useCallback(
    (id: string, value: number) => {
      setInputState((prev) => ({ ...prev, [id]: value }));
    },
    [],
  );

  const handleCheckboxChange = useCallback(
    (id: string, checked: boolean) => {
      setInputState((prev) => ({ ...prev, [id]: checked }));
    },
    [],
  );

  const handleReset = useCallback(() => {
    setInputState(buildInitialState(data.inputs));
  }, [data.inputs]);

  const getShareUrl = useCallback(() => {
    if (typeof window === "undefined") {
      throw new Error("PricingCalculator: window is not available");
    }
    const url = new URL(window.location.href.split("#")[0].split("?")[0]);
    for (const input of data.inputs) {
      const value = inputState[input.id];
      url.searchParams.set(input.id, String(value));
    }
    url.hash = "calculator";
    return url.toString();
  }, [data.inputs, inputState]);

  const handleShare = useCallback(() => {
    const shareUrl = getShareUrl();
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [getShareUrl]);

  if (typeof sheets !== "number") {
    throw new Error("PricingCalculator: sheets input is missing");
  }
  if (typeof columns !== "number") {
    throw new Error("PricingCalculator: columns input is missing");
  }
  if (typeof extraSource !== "boolean") {
    throw new Error("PricingCalculator: extraSource input is missing");
  }
  const { pricingModel } = data;
  const isOnRequest = sheets > pricingModel.maxPricedSheets;

  const columnsAboveBase = Math.max(0, columns - pricingModel.baseColumns);
  const columnsInLowerBand = Math.max(
    0,
    Math.min(columnsAboveBase, pricingModel.columnThreshold - pricingModel.baseColumns),
  );
  const columnsInUpperBand = Math.max(
    0,
    columns - pricingModel.columnThreshold,
  );
  const baseByColumns =
    pricingModel.basePrice +
    columnsInLowerBand * pricingModel.pricePerAdditionalColumn +
    columnsInUpperBand * pricingModel.pricePerAdditionalColumnAboveThreshold;
  const sheetMultiplier = pricingModel.sheetMultipliers[String(sheets) as "1" | "2" | "3"];

  if (!isOnRequest && typeof sheetMultiplier !== "number") {
    throw new Error(`PricingCalculator: no sheet multiplier for ${sheets} sheets`);
  }

  const numericPrice = isOnRequest
    ? null
    : Math.round(
        baseByColumns *
          sheetMultiplier *
          (extraSource ? pricingModel.extraSourceMultiplier : 1),
      );
  const agencyPriceTiers = pricingModel.agencyPriceTiers;
  const agencyPrice =
    numericPrice !== null
      ? agencyPriceTiers !== undefined && agencyPriceTiers[String(sheets)] !== undefined
        ? agencyPriceTiers[String(sheets)]
        : Math.round(numericPrice * data.agencyMultiplier)
      : null;
  const savings =
    numericPrice !== null && agencyPrice !== null
      ? agencyPrice - numericPrice
      : null;

  /* The visitor already answered these questions in the calculator; repeating
     them in the lead form is friction. Hand the configuration over as prose so
     it survives into the CRM message field verbatim. */
  const buildQuoteSummary = () => {
    const labelFor = (id: string) =>
      data.inputs.find((input) => input.id === id)?.label ?? id;
    const lines = [
      "Konfiguration aus dem Preisrechner:",
      `· ${labelFor("sheets")}: ${sheets}`,
      `· ${labelFor("columns")}: ${columns}`,
      `· Zusätzliche CSV-/Web-Datenquelle: ${extraSource ? "ja" : "nein"}`,
      numericPrice !== null
        ? `· ${data.labels.yours}: ${formatPrice(numericPrice, pricingModel.currency)}`
        : `· ${data.labels.yours}: Auf Anfrage`,
    ];
    return `${lines.join("\n")}\n\n`;
  };

  return (
    <section
      id="calculator"
      className="py-16 md:py-24 bg-white"
      aria-labelledby="calculator-title"
    >
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="text-center mb-12">
          <SeoChip text={data.seoChip} icon={Zap} />
          <h2
            id="calculator-title"
            className="text-3xl md:text-4xl font-bold text-abis-primary mt-4 mb-3"
          >
            {data.title}
          </h2>
          <p className="text-xl text-gray-700">{data.subtitle}</p>
          <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
            {data.description}
          </p>
        </div>

        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-200 shadow-xl p-6 md:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <h3 className="flex items-center gap-2 text-lg font-bold text-abis-primary leading-none">
              <Calculator className="w-5 h-5 text-abis-primary flex-shrink-0" />
              Ihr Dashboard konfigurieren
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all duration-200 bg-gray-50 text-gray-500 border-gray-200 hover:bg-abis-primary/5 hover:text-abis-primary hover:border-abis-primary/20"
                title="Standardwerte wiederherstellen"
                type="button"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Zurücksetzen
              </button>
              <button
                onClick={handleShare}
                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all duration-200 ${
                  copied
                    ? "bg-green-50 text-green-700 border-green-200"
                    : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-abis-primary/5 hover:text-abis-primary hover:border-abis-primary/20"
                }`}
                title="Link mit aktuellen Werten kopieren"
                type="button"
              >
                {copied ? (
                  <CheckCircle className="w-3.5 h-3.5" />
                ) : (
                  <Link2 className="w-3.5 h-3.5" />
                )}
                {copied ? "Kopiert!" : "Teilen"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="space-y-6">
              {data.inputs.map((input) => {
                if (input.type === "range") {
                  const rangeInput = input as RangeInput;
                  const currentVal = inputState[input.id] as number;

                  return (
                    <div key={input.id}>
                      <label
                        htmlFor={`slider-${input.id}`}
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        {input.label}
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          id={`slider-${input.id}`}
                          type="range"
                          min={rangeInput.min}
                          max={rangeInput.max}
                          step={rangeInput.step}
                          value={currentVal}
                          onChange={(e) =>
                            handleRangeChange(input.id, Number(e.target.value))
                          }
                          className="flex-1 accent-abis-primary cursor-pointer touch-none"
                          aria-label={input.label}
                        />
                        <input
                          type="number"
                          min={rangeInput.min}
                          max={rangeInput.max}
                          step={rangeInput.step}
                          value={currentVal}
                          onChange={(e) =>
                            handleRangeChange(input.id, Number(e.target.value))
                          }
                          className="w-16 text-center font-bold text-abis-primary text-sm tabular-nums bg-white border border-gray-200 rounded-lg py-1 focus:border-abis-primary focus:ring-1 focus:ring-abis-primary/20 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          aria-label={`${input.label} Eingabe`}
                        />
                      </div>
                      {rangeInput.displayMin && rangeInput.displayMax && (
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-gray-400">
                            {rangeInput.displayMin}
                          </span>
                          <span className="text-xs text-gray-400">
                            {rangeInput.displayMax}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                }

                if (input.type === "checkbox") {
                  const checked = inputState[input.id] as boolean;
                  return (
                    <div
                      key={input.id}
                      className="flex items-start gap-3 bg-white rounded-xl border border-gray-200 p-4"
                    >
                      <div className="relative flex-shrink-0 mt-0.5">
                        <input
                          id={`check-${input.id}`}
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            handleCheckboxChange(input.id, e.target.checked)
                          }
                          className="w-4 h-4 min-w-4 min-h-4 rounded border-gray-300 text-abis-primary focus:ring-abis-primary cursor-pointer"
                          aria-label={input.label}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label
                          htmlFor={`check-${input.id}`}
                          className="text-sm font-medium text-gray-700 cursor-pointer leading-relaxed"
                        >
                          {input.label}
                        </label>
                        {input.footnote && (
                          <p className="text-xs text-gray-400 leading-relaxed">
                            {input.footnote}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                }

                return null;
              })}
            </div>

            <div className="space-y-4">
              {isOnRequest ? (
                <div className="bg-abis-primary rounded-xl p-6 border border-abis-primary flex flex-col gap-4 h-full">
                  <div>
                    <p className="text-xs font-semibold text-abis-accent uppercase tracking-wide mb-2">
                      Individuelles Angebot
                    </p>
                    <p className="text-3xl font-bold text-white leading-none mb-3">
                      Auf Anfrage
                    </p>
                    <p className="text-sm text-white/80 leading-relaxed">
                      {data.enterpriseCta.description}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 mt-auto">
                    <Link
                      href={data.enterpriseCta.secondaryCta.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full btn btn-hero font-bold text-base py-4 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                      aria-label={data.enterpriseCta.ctaText}
                    >
                      {data.enterpriseCta.ctaText}
                      <ArrowRight className="w-5 h-5" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div className="bg-abis-primary/5 rounded-xl p-4 border border-abis-primary/20">
                    <p className="text-xs font-semibold text-abis-primary uppercase tracking-wide mb-1">
                      {data.labels.yours}
                    </p>
                    <p className="text-4xl md:text-5xl font-bold text-abis-primary leading-none tabular-nums">
                      {formatPrice(numericPrice!, pricingModel.currency)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Dynamischer Festpreis · Einmalpreis
                    </p>
                  </div>

                  <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                    <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1">
                      {data.labels.agency}
                    </p>
                    <p className="text-2xl font-bold text-red-700 tabular-nums">
                      ~{formatPrice(agencyPrice!, pricingModel.currency)}
                    </p>
                  </div>

                  <div className="bg-green-50 rounded-xl p-4 border border-green-100 ring-2 ring-green-200">
                    <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">
                      {data.labels.savings}
                    </p>
                    <p className="text-2xl font-bold text-green-700 tabular-nums">
                      {formatPrice(savings!, pricingModel.currency)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      gegenüber klassischem Agenturansatz
                    </p>
                  </div>

                  <div className="pt-2">
                    {onRequestQuote ? (
                      <button
                        type="button"
                        onClick={() => onRequestQuote(buildQuoteSummary())}
                        className="flex items-center justify-center gap-2 w-full btn btn-hero font-bold text-base py-4 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-abis-accent/50"
                        aria-label={`${data.ctaText} für ${formatPrice(numericPrice!, pricingModel.currency)}`}
                      >
                        {data.ctaText}
                        <ArrowRight className="w-5 h-5" aria-hidden="true" />
                      </button>
                    ) : (
                      <Link
                        href={data.ctaHref}
                        className="flex items-center justify-center gap-2 w-full btn btn-hero font-bold text-base py-4 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-abis-accent/50"
                        aria-label={`${data.ctaText} für ${formatPrice(numericPrice!, pricingModel.currency)}`}
                      >
                        {data.ctaText}
                        <ArrowRight className="w-5 h-5" aria-hidden="true" />
                      </Link>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="text-sm text-gray-500 text-left max-w-4xl mx-auto leading-relaxed">
            {data.aeoNote !== undefined && (
              <p className="whitespace-pre-line">{data.aeoNote}</p>
            )}

            <details className="group mt-5">
              <summary className="cursor-pointer list-none flex items-center gap-2 text-sm font-semibold text-abis-primary hover:text-abis-accent transition-colors select-none">
                <ChevronRight
                  className="w-4 h-4 shrink-0 self-center transition-transform group-open:rotate-90"
                  aria-hidden="true"
                />
                <span className="m-0 text-sm font-semibold leading-none">
                  {data.priceExamples.title}
                </span>
              </summary>
              <div className="mt-0 overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th scope="col" className="px-5 py-3 text-left font-semibold text-gray-700">
                        Konfiguration
                      </th>
                      <th scope="col" className="px-5 py-3 text-left font-semibold text-gray-700">
                        Paket
                      </th>
                      <th scope="col" className="px-5 py-3 text-left font-semibold text-gray-700">
                        Einmalpreis
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.priceExamples.rows.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                        <td className="px-5 py-3 text-gray-700">{row.config}</td>
                        <td className="px-5 py-3 text-gray-600">{row.package}</td>
                        <td className="px-5 py-3 font-semibold text-abis-primary">{row.price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-gray-500 italic">{data.priceExamples.footnote}</p>
            </details>
          </div>
        </div>
      </div>
    </section>
  );
}
